import { FC } from 'react';

import { Button, TextButton, Heading } from '../../../shared/ui';

import { useClientAssignmentSelection } from './context';

interface IProps {
  onSend: () => void;
  onSaveDraft: () => void;
  onRecall: () => void;
  isSubmitting?: boolean;
  isSent?: boolean;
  isRestoring?: boolean;
}

/** Панель «Отправка клиенту» — сводка выбранных инструментов и заданий. */
export const ClientSendPanel: FC<IProps> = ({ onSend, onSaveDraft, onRecall, isSubmitting, isSent, isRestoring }) => {
  const { items, totalCount } = useClientAssignmentSelection();
  const isEmpty = items.length === 0;
  const isLocked = Boolean(isSubmitting || isSent || isRestoring);

  return (
    <aside className="@container rounded-card border border-line bg-white p-[22px] shadow-raised">
      {/* Широкая панель (встала под списком): выбранное слева, действия справа. Узкая — всё столбцом. */}
      <div className="flex flex-col gap-4 @xl:grid @xl:grid-cols-[minmax(0,1fr)_minmax(220px,280px)] @xl:items-start @xl:gap-x-6">
        <div className="flex items-center justify-between gap-3 @xl:col-span-2">
          <Heading size="md" as="h3">
            Отправка клиенту
          </Heading>
          <span className="shrink-0 text-ui-s text-ink-subtle">
            {items.length} из {totalCount}
          </span>
        </div>

        <div className="flex min-w-0 flex-col gap-2">
          {items.map((item) => (
            <div key={item.id} className="flex items-start gap-2.5 rounded-control bg-surface-muted p-[12px_14px]">
              <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="text-body-l-strong text-ink">{item.title}</span>
                <span className="break-words text-ui-s leading-snug text-ink-muted">{item.summary}</span>
              </div>
            </div>
          ))}

          {isEmpty && (
            <p className="rounded-control border border-dashed border-line-strong p-[18px_14px] text-center text-ui leading-normal text-ink-subtle">
              Ничего не выбрано. Отметьте инструмент или домашнее задание в списке.
            </p>
          )}
        </div>

        <div className="h-px bg-line @xl:hidden" />

        <div className="flex min-w-0 flex-col gap-4">
          {isSent ? (
            <Button size="md" variant="outline" fullWidth disabled={isSubmitting} onClick={onRecall}>
              {isSubmitting ? 'Отзыв…' : 'Отозвать задание'}
            </Button>
          ) : (
            <>
              <Button size="md" variant="primary" fullWidth disabled={isEmpty || isLocked} onClick={onSend}>
                {isSubmitting ? 'Отправка…' : isEmpty ? 'Отправить клиенту' : `Отправить клиенту · ${items.length}`}
              </Button>

              <TextButton tone="muted" underline="always" className="text-ui" onClick={onSaveDraft} disabled={isLocked}>
                Сохранить черновик
              </TextButton>
            </>
          )}

          <p className="text-ui-s leading-normal text-ink-subtle">
            {isSent
              ? 'Задание отправлено клиенту. Отзовите его, чтобы изменить текст и отправить заново.'
              : 'Отправка ещё не выполнена. Клиент увидит инструменты и задание одним сообщением.'}
          </p>
        </div>
      </div>
    </aside>
  );
};
